import {
  Button,
  Icons,
  Positions,
  Sizes,
} from '@sede-enterprise/shell-ds-react-framework';
import { Skeleton } from 'components/storybook-migration/skeleton/Skeleton';
import { useRevenueBreakdown } from 'hooks/useRevenueBreakdown/useRevenueBreakdown';
import {
  BreakdownTooltips,
  revenueBreakdownColorMap,
} from 'pages/Optimisation/components/SummaryCards/RevenueBreakdownCard/BreakdownTooltips';
import {
  StackedBarItemBorder,
  StackedBarItemZero,
  StackedBarWrapper,
} from 'pages/Optimisation/components/SummaryCards/RevenueBreakdownCard/RevenueBreakdownCard.styled';
import RevenueExpandedContent from 'pages/Optimisation/components/SummaryCards/RevenueBreakdownCard/RevenueExpandedContent';
import {
  CardExpandableContentContainer,
  CardStyled,
  SummaryCardWrapper,
} from 'pages/Optimisation/components/SummaryCards/SummaryCard.styled';
import { SummaryCardTitle } from 'pages/Optimisation/components/SummaryCards/SummaryCardTitle/SummaryCardTitle';
import { SummaryCardProps } from 'pages/Optimisation/types';
import { useStore } from 'store';
import { useShowBasedOnWidth } from 'utils/useShowBasedOnWidth';

export const RevenueBreakdownCard = ({
  data,
  loading,
  marketFilter,
}: SummaryCardProps) => {
  const {
    handleExpand,
    isOpen,
    zeroLinePosition,
    marketData,
    sortedMarketData,
    totalRevenue,
    getRevenuePercentage,
    getPercentageOfGainOrLoss,
  } = useRevenueBreakdown(data, loading, marketFilter);
  const { elementRef: cardRef } = useShowBasedOnWidth(348);

  const [appTheme] = useStore((state) => [state.appTheme]);

  return (
    <div style={{ position: 'relative', flex: '1 1 276px' }}>
      <CardStyled
        data-testid="card-testId"
        ref={cardRef ?? null}
        elevated={true}
        size={Sizes.Small}
      >
        <SummaryCardWrapper>
          <SummaryCardTitle
            style={{ marginBottom: 8, marginTop: -8 }}
            title={'Revenue breakdown'}
            leftContent={
              <Button
                variant={'transparent'}
                onClick={handleExpand}
                icon={isOpen ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
                iconPosition={Positions.Right}
                size={Sizes.Small}
              >
                <b>{isOpen ? 'Hide' : 'Amounts'}</b>
              </Button>
            }
          />
          {data && !loading ? (
            <StackedBarWrapper data-testid="stacked-bar-wrapper">
              <BreakdownTooltips
                sortedMarketData={sortedMarketData}
                getRevenuePercentage={getRevenuePercentage}
                getPercentageOfGainOrLoss={getPercentageOfGainOrLoss}
              />
              <StackedBarItemBorder
                data-testid="zero-line-span"
                $width={zeroLinePosition}
              >
                <svg
                  width="2"
                  height="32"
                  viewBox="0 0 2 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.999996 0L1 32"
                    stroke="#FFFFFF"
                    strokeWidth="2"
                    strokeMiterlimit="16"
                    strokeLinejoin="round"
                    strokeDasharray="1 4"
                    strokeLinecap="round"
                  />
                </svg>
                <StackedBarItemZero $theme={appTheme}>£0</StackedBarItemZero>
              </StackedBarItemBorder>
            </StackedBarWrapper>
          ) : (
            <Skeleton data-testid="skeleton" width="100%" height="35px" />
          )}
        </SummaryCardWrapper>
      </CardStyled>
      <CardExpandableContentContainer
        style={{ maxHeight: isOpen ? '100vh' : 0 }}
        aria-expanded={isOpen}
        data-testid="card-expanded-container"
      >
        <RevenueExpandedContent
          hint="*These amounts assume 100% asset performance."
          colorMap={revenueBreakdownColorMap}
          marketData={marketData}
          hasNegativeRevenue={zeroLinePosition !== 0}
        />
      </CardExpandableContentContainer>
    </div>
  );
};
